
import { useNavigate } from "react-router-dom";
import { LogIn, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { sanitizeBlogContent } from "@/utils/sanitizer";

interface BlogPostContentProps {
  content: string;
  textDirection?: "ltr" | "rtl";
  isLoggedIn: boolean;
}

const BlogPostContent = ({ content, textDirection, isLoggedIn }: BlogPostContentProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  if (isLoggedIn) {
    return (
      <div 
        className={`blog-content prose prose-lg max-w-none mb-12 ${textDirection === 'rtl' ? 'rtl' : 'ltr'} ${isMobile ? 'mobile-content' : ''}`} 
        dir={textDirection || 'ltr'}
      >
        <div dangerouslySetInnerHTML={{ __html: sanitizeBlogContent(content) }} />
      </div>
    );
  }
  
  const getPreviewContent = () => {
    if (!content || content.trim() === '') {
      return '<p>Sign in to read this premium article and unlock all content.</p>';
    }
    
    try {
      // Create a temporary document to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      
      // Get only the body content
      const bodyContent = doc.body.innerHTML;
      
      // Create another temporary div to work with the content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = bodyContent;
      
      // Define which elements we want to extract for preview
      const contentElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, pre, code, div');
      
      // If no structured content is found, handle plain text
      if (contentElements.length === 0) {
        const textContent = tempDiv.textContent || '';
        const preview = textContent.substring(0, 700).trim();
        if (preview.length === 0) {
          return '<p>Sign in to read this premium article and unlock all content.</p>';
        }
        return `<p>${preview}${preview.length > 0 ? '...' : ''}</p>`;
      }
      
      // Calculate how many elements to show in the preview (30% or at least 1)
      const elementsToShow = Math.max(Math.ceil(contentElements.length * 0.3), 1);
      
      // Extract the complete HTML of the elements we want to show
      let previewContent = '';
      for (let i = 0; i < elementsToShow && i < contentElements.length; i++) {
        previewContent += contentElements[i].outerHTML;
      }
      
      return previewContent;
    } catch (error) {
      console.error("Error generating preview content:", error);
      return '<p>Preview content unavailable. Sign in to read the full article.</p>';
    }
  };
  
  const previewContent = getPreviewContent();
  
  return (
    <div className="relative">
      <div 
        className={`blog-content prose prose-lg max-w-none mb-8 ${textDirection === 'rtl' ? 'rtl' : 'ltr'} ${isMobile ? 'mobile-content' : ''}`} 
        dir={textDirection || 'ltr'}
      >
        <div dangerouslySetInnerHTML={{ __html: sanitizeBlogContent(previewContent) }} />
      </div>
      
      <div className="mt-8">
        <div className="bg-white px-4 sm:px-6 py-5 rounded-lg shadow-lg border border-gray-100 text-center">
          <div className="flex items-center justify-center text-gray-800 mb-4">
            <Lock className="mr-2 h-5 w-5 text-[#34BF49]" />
            <span className="font-medium">Premium Content</span>
          </div>
          <p className="text-gray-600 mb-4">
            Sign in to read the full article and unlock all content.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#34BF49] hover:bg-[#2da940] text-white font-medium px-4 sm:px-6 py-2 w-full sm:w-auto"
          >
            <LogIn className="mr-2 h-4 w-4" />
            Login to Continue Reading
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogPostContent;
